import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Brain, Sparkles, TrendingUp, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";

const REGULARITY_META = {
  regular: { label: "Regular", color: "text-emerald-300", bg: "bg-emerald-500/15", border: "border-emerald-500/30" },
  somewhat_regular: { label: "Somewhat Regular", color: "text-amber-300", bg: "bg-amber-500/15", border: "border-amber-500/30" },
  irregular: { label: "Irregular", color: "text-rose-300", bg: "bg-rose-500/15", border: "border-rose-500/30" },
  insufficient_data: { label: "Learning Your Rhythm", color: "text-mystic-200", bg: "bg-mystic-500/15", border: "border-mystic-500/30" },
};

const CONFIDENCE_META = {
  high: { label: "High Confidence", dots: 3 },
  medium: { label: "Medium Confidence", dots: 2 },
  low: { label: "Building Data", dots: 1 },
};

const CycleIntelligenceCard = ({ prediction }) => {
  if (!prediction) return null;

  const isAnalysis = prediction.source === "analysis";
  const reg = REGULARITY_META[prediction.regularity] || REGULARITY_META.insufficient_data;
  const conf = CONFIDENCE_META[prediction.confidence] || CONFIDENCE_META.low;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      data-testid="cycle-intelligence-card"
    >
      <Card className={`glass ${reg.border} overflow-hidden`}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <CardTitle className="text-white flex items-center gap-2">
              <Brain className="w-5 h-5 text-mystic-300" />
              Your Cycle Intelligence
            </CardTitle>
            <Badge className={`${reg.bg} ${reg.color} border-0`} data-testid="cycle-regularity-badge">
              {reg.label}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-mystic-900/40 p-3 border border-white/5">
              <p className="text-mystic-300 text-xs uppercase tracking-wider mb-1">Avg Cycle</p>
              <p className="text-white text-xl font-semibold">
                {prediction.average_cycle_length} <span className="text-mystic-300 text-sm font-normal">days</span>
              </p>
            </div>
            <div className="rounded-xl bg-mystic-900/40 p-3 border border-white/5">
              <p className="text-mystic-300 text-xs uppercase tracking-wider mb-1">Avg Period</p>
              <p className="text-white text-xl font-semibold">
                {prediction.average_period_length} <span className="text-mystic-300 text-sm font-normal">days</span>
              </p>
            </div>
          </div>

          <div className="rounded-xl bg-gradient-to-br from-pink-500/10 to-purple-500/10 p-3 border border-pink-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-pink-200 text-xs uppercase tracking-wider mb-1 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Next Period
                </p>
                <p className="text-white text-lg font-semibold" data-testid="next-period-date">
                  {prediction.next_period_start}
                </p>
              </div>
              <div className="text-right">
                <p className="text-pink-200 text-xs uppercase tracking-wider mb-1">Fertile Window</p>
                <p className="text-mystic-100 text-sm">
                  {prediction.fertile_window_start} → {prediction.fertile_window_end}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-1.5" data-testid="prediction-confidence">
              <TrendingUp className="w-3 h-3 text-mystic-300" />
              <span className="text-mystic-200/80">{conf.label}</span>
              <span className="flex gap-0.5 ml-1">
                {[1, 2, 3].map(i => (
                  <span
                    key={i}
                    className={`w-1.5 h-1.5 rounded-full ${i <= conf.dots ? 'bg-mystic-300' : 'bg-white/10'}`}
                  />
                ))}
              </span>
            </div>
            {isAnalysis && prediction.periods_analyzed > 0 && (
              <span className="text-mystic-200/60">
                Based on {prediction.periods_analyzed} {prediction.periods_analyzed === 1 ? 'period' : 'periods'}
              </span>
            )}
          </div>

          {!isAnalysis && prediction.message && (
            <p className="text-mystic-200/60 text-xs italic">{prediction.message}</p>
          )}

          <Link
            to="/calendar"
            className="flex items-center justify-center gap-1 text-sm text-mystic-300 hover:text-mystic-100 transition-colors pt-1"
            data-testid="cycle-intelligence-log-link"
          >
            Log a new period <ChevronRight className="w-4 h-4" />
          </Link>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default CycleIntelligenceCard;
